<template>
  <WdsModal
    custom-size="866px"
    :show="isOpen"
    no-close-icon
    :z-index="4000"
    @cancel="emit('cancel')"
  >
    <div>
      <WdsButtonGhost secondary @click="cancelPaymentRequest">
        <WdsStack gap-sm>
          <WdsIcon :size="16" :name="WdsIconNameEnum.UNDO" />
          {{ $t('Cancel') }}
        </WdsStack>
      </WdsButtonGhost>
      <div class="tw-mt-4">
        <WdsHeading as="h1" type="display">
          {{ $t('Payment request') }}
          <span v-if="billId">#{{ billId }}</span>
        </WdsHeading>
        <WdsText v-if="!store.isFetchingData" xl class="tw-mt-2">
          <div class="tw-truncate tw-max-w-full" :title="`${store.companyName} / ${store.jobName}`">
            {{ store.companyName }} / {{ store.jobName }}
          </div>
        </WdsText>
        <WdsTag v-if="!persistedBill || isDraft" sm warning>
          {{ !persistedBill ? $t('Unsaved') : $t('Draft') }}
        </WdsTag>
        <WdsTag v-else-if="isApproved" sm warning>
          {{ $t('Approved') }}
        </WdsTag>
      </div>
    </div>
    <template v-if="$slots['blocking-alerts'] && isEditable">
      <slot name="blocking-alerts" />
    </template>
    <template v-else>
      <template v-if="$slots['non-blocking-alerts']">
        <div class="tw-my-8.5">
          <slot name="non-blocking-alerts" />
        </div>
      </template>
      <WdsSpinner v-if="store.isFetchingData" indeterminate />
      <div v-if="!store.isFetchingData">
        <BillCreateComplianceNotices v-if="isEditable" :hire-id="hireId" class="tw-my-8.5" />
        <WdsStack direction-column class="payment-request-form-container">
          <!-- START SECTION -->
          <WdsStack gap-md direction-column>
            <WdsHeading type="subtitle">{{ $t('Billing period') }}</WdsHeading>
            <WdsStack class="payment-request-input-section" direction-column gap-lg>
              <WdsStack justify-space-between class="tw-grow">
                <WdsText large>
                  <template #default>{{ $t('From date') }}</template>
                  <template #subText>
                    <span class="tw-text-base">{{ $t('The first billable day') }}</span>
                  </template>
                </WdsText>
                <PickerDatetime
                  v-model="store.paymentRequestForm.dateStart"
                  name="date_start"
                  format="YYYY-MM-DD"
                  :error="validationStore.v$.dateStart.$errors?.[0]?.$message"
                  :disabled="!isEditable"
                  @update:model-value="validationStore.v$.dateEnd.$touch()"
                  @blur="validationStore.v$.dateStart.$touch()"
                />
              </WdsStack>
              <WdsStack justify-space-between class="tw-grow">
                <WdsText large>
                  <template #default>
                    {{ $t('To date') }}
                  </template>
                  <template #subText>
                    <span class="tw-text-base">{{ $t('The last billable day') }}</span>
                  </template>
                </WdsText>
                <PickerDatetime
                  v-model="store.paymentRequestForm.dateEnd"
                  name="date_end"
                  format="YYYY-MM-DD"
                  :min-date="
                    store.paymentRequestForm.dateEnd ? store.paymentRequestForm.dateStart : ''
                  "
                  :error="validationStore.v$.dateEnd.$errors?.[0]?.$message"
                  :disabled="!isEditable"
                  @blur="validationStore.v$.dateEnd.$touch()"
                />
              </WdsStack>
            </WdsStack>
            <WdsAlert :hidden="!store.paymentRequestForm.dateEnd">
              {{ $t("Here's a breakdown") }}
              <template #description>
                {{ $tc(':count day total | :count days total', Number(dateRange.days)) }}:
                {{ dateRange.week_days }}x {{ $t('Monday') }}-{{ $t('Friday') }},
                {{ dateRange.weekend_days }}x {{ $t('Saturday') }} & {{ $t('Sunday') }}
              </template>
            </WdsAlert>
          </WdsStack>
          <!-- END SECTION-->
          <!-- START SECTION -->
          <WdsStack gap-md direction-column>
            <WdsHeading type="subtitle">{{ $t('Charge for your work') }}</WdsHeading>
            <WdsSegmentedControl
              v-if="allowTimeXRatePaymentRequests && isEditable"
              :default-active-key="store.paymentRequestType"
              full-width
              :options="[
                {
                  optionKey: PaymentRequestTypeEnum.TIME_X_RATE,
                  title: $t('Time x Rate'),
                  icon: 'calendar',
                },
                {
                  optionKey: PaymentRequestTypeEnum.SPECIFY_AMOUNT,
                  title: $t('Specify Amount'),
                  icon: 'currency',
                },
              ]"
              @click="updatePaymentRequestType"
            />
            <Rate />
            <WdsStack class="payment-request-input-section" direction-column gap-lg>
              <WdsStack
                v-if="store.paymentRequestType === PaymentRequestTypeEnum.TIME_X_RATE"
                justify-space-between
                class="tw-grow"
              >
                <WdsText large>
                  <template #default>{{ $t('Billable time') }}</template>
                  <template #subText>
                    <span class="tw-text-base">
                      {{
                        $t('Number of :rateUnits worked in this period', {
                          rateUnits: $t(rateUnits),
                        })
                      }}
                    </span>
                  </template>
                </WdsText>
                <div class="tw-w-[200px] tw-max-w-full">
                  <InputDecimal
                    v-model:formatted="store.paymentRequestForm.billableTime"
                    name="billable-time"
                    :fraction-min="0"
                    :fraction-max="99"
                    :min="0.0"
                    decimal-point="."
                    placeholder="0"
                    fraction-placeholder="00"
                    :error="
                      validationStore.v$.billableTime.$error
                        ? expensesStore.enableExpenseReports
                          ? $t('Add billable time or attach expenses')
                          : $t('Add billable time')
                        : undefined
                    "
                    :disabled="!isEditable"
                    @blur="validationStore.v$.billableTime.$touch()"
                  />
                </div>
              </WdsStack>
              <WdsStack v-else justify-space-between class="tw-grow">
                <WdsText large>
                  <template #default>{{ $t('Billable total') }}</template>
                  <template #subText>
                    <span class="tw-text-base">{{ $t('The amount to charge') }}</span>
                  </template>
                </WdsText>
                <WdsInputCurrency
                  v-model="store.paymentRequestForm.billableTotal"
                  class="tw-w-[200px] tw-max-w-max"
                  :config="inputCurrencyConfig"
                  :error="
                    validationStore.v$.billableTotal.$error
                      ? expensesStore.enableExpenseReports
                        ? $t('Add a billable total or attach expenses')
                        : $t('Add a billable total')
                      : false
                  "
                  :disabled="!isEditable"
                  @blur="validationStore.v$.billableTotal.$touch()"
                />
              </WdsStack>
              <WdsStack justify-space-between class="tw-grow">
                <WdsText large>
                  <template #default>{{ $t('Timesheet') }}</template>
                  <template #subText>
                    <span class="tw-text-base">{{ $t('Report specific time worked') }}</span>
                  </template>
                </WdsText>
                <WdsSelect
                  v-model="timesheetsStore.timesheetType"
                  class="tw-w-[200px] tw-max-w-full"
                  :list="timesheetsStore.timesheetTypeOptions"
                  :error="timesheetErrors"
                  :disabled="!isEditable"
                />
              </WdsStack>
              <Transition name="fade">
                <WdsStack
                  v-show="timesheetsStore.timesheetType.value !== TimesheetTypeEnum.NONE"
                  direction-column
                  gap-sm
                >
                  <TimesheetInputs :timesheet-type="timesheetsStore.timesheetType.value" />
                </WdsStack>
              </Transition>
              <WdsStack direction-column gap-sm>
                <WdsStack justify-space-between class="tw-grow tw-text-gray-600">
                  <span>
                    {{
                      store.paymentRequestType === PaymentRequestTypeEnum.TIME_X_RATE
                        ? $t('Billable time')
                        : $t('Billable total')
                    }}
                  </span>
                  <span>{{ store.currency }} {{ calculationsStore.totalCharged }}</span>
                </WdsStack>
                <WdsStack
                  v-for="taxLine in taxStore.allTaxLines"
                  :key="taxLine.terminology"
                  justify-space-between
                  class="tw-grow tw-text-gray-600"
                >
                  <span>{{ taxLine.terminology }} ({{ taxLine.ratePercentage }}%)</span>
                  <span>{{ store.currency }} {{ taxLine.amountCharged }}</span>
                </WdsStack>
                <WdsStack justify-space-between class="tw-grow">
                  <span>{{ $t('Subtotal') }}</span>
                  <span>{{ store.currency }} {{ calculationsStore.subTotal }}</span>
                </WdsStack>
              </WdsStack>
            </WdsStack>
          </WdsStack>
          <!-- END SECTION-->
          <Expenses
            v-if="
              (expensesStore.enableExpenseReports && isEditable) ||
              (!isEditable && expensesStore.expenseReports.length > 0)
            "
            @exit-to-expenses="saveAndGoToExpenses"
          />
          <!-- START SECTION -->
          <WdsStack gap-md direction-column>
            <WdsInputArea
              v-model="store.paymentRequestForm.comments"
              :label="$t('Comments (optional)')"
              :placeholder="$t('Add additional information...')"
              :disabled="!isEditable"
            ></WdsInputArea>
          </WdsStack>
          <WdsAlert v-if="store.market === 'ca'" info icon>
            {{
              $t(
                'Invoice total is exclusive of sales tax. The applicable sales tax will be added to the final invoice.',
              )
            }}
          </WdsAlert>
          <!-- END SECTION-->
          <WdsStack class="tw-p-4 tw-bg-indigo-50" direction-column gap-sm>
            <span class="tw-text-indigo-600 tw-text-lg">{{ $t('Overview') }}</span>
            <WdsStack justify-space-between>
              <span class="tw-text-gray-850">
                {{
                  store.paymentRequestType === PaymentRequestTypeEnum.TIME_X_RATE
                    ? $t('Billable time')
                    : $t('Billable total')
                }}
              </span>
              <span class="tw-text-gray-850">
                {{ store.currency }} {{ calculationsStore.totalCharged }}
              </span>
            </WdsStack>
            <WdsStack
              v-for="taxLine in taxStore.allTaxLines"
              :key="taxLine.terminology"
              justify-space-between
            >
              <span class="tw-text-gray-850">
                {{ taxLine.terminology }} ({{ taxLine.ratePercentage }}%)
              </span>
              <span class="tw-text-gray-850">{{ store.currency }} {{ taxLine.amountCharged }}</span>
            </WdsStack>
            <WdsStack
              v-if="expensesStore.enableExpenseReports && !!expensesStore.expenseReports.length"
              justify-space-between
            >
              <span class="tw-text-gray-850">{{ $t('Expenses') }}</span>
              <span class="tw-text-gray-850">
                {{ store.currency }} {{ calculationsStore.totalExpensedAmountFormatted }}
              </span>
            </WdsStack>
            <WdsStack justify-space-between class="emphasised-amount-line">
              <span>{{ $t('Invoice total') }}</span>
              <span>{{ store.currency }} {{ calculationsStore.invoiceTotal }}</span>
            </WdsStack>
            <template v-if="store.worksomeFee">
              <WdsStack justify-space-between>
                <span class="tw-text-gray-850">
                  {{ $t('Worksome fee') }} ({{ calculationsStore.worksomeFeePercentage }}%)
                </span>
                <span class="tw-text-gray-850">
                  {{ store.currency }} {{ calculationsStore.feeTotal }}
                </span>
              </WdsStack>
              <WdsStack justify-space-between class="emphasised-amount-line">
                <span>{{ $t('Your earnings') }}</span>
                <span>{{ store.currency }} {{ calculationsStore.earningsTotal }}</span>
              </WdsStack>
            </template>
          </WdsStack>
          <WdsAlert warning icon :hidden="!validationStore.v$?.$errors?.length">
            {{ $t("You're missing some information") }}
          </WdsAlert>
        </WdsStack>
      </div>
    </template>
    <template v-if="!$slots['blocking-alerts'] && !store.isFetchingData && isEditable" #footer>
      <WdsStack justify-space-between align-center>
        <WdsSpinner v-if="store.isSubmitting" indeterminate />
        <WdsText v-else-if="!persistedBill || isDraft" small>
          {{ !persistedBill ? $t('Unsaved') : $t('Last saved') }}
          <template #subText>
            {{ !persistedBill ? $t('Send or save as draft to keep changes') : updatedAt }}
          </template>
        </WdsText>
        <span v-else></span>
        <Submit ref="submitFormRef" />
      </WdsStack>
    </template>
  </WdsModal>
</template>

<script setup lang="ts">
import {
  WdsAlert,
  WdsButtonGhost,
  WdsHeading,
  WdsIcon,
  WdsIconNameEnum,
  WdsInputArea,
  WdsInputCurrency,
  WdsModal,
  WdsSegmentedControl,
  WdsSelect,
  WdsSpinner,
  WdsStack,
  WdsTag,
  WdsText,
} from '@worksome/wds-components'
import { computed, ref, watch } from 'vue'
import moment from 'moment'
import { useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import PickerDatetime from '@/components/PickerDatetime.vue'
import InputDecimal from '@/components/InputDecimal.vue'
import Rate from '@/components/PaymentRequest/RateDisplay.vue'
import useDateRange from '@/composables/useDateRange'
import TimesheetInputs from '@/components/PaymentRequest/TimesheetInputs.vue'
import Expenses from '@/components/PaymentRequest/Expenses/Expenses.vue'
import { usePaymentRequestStore } from '@/components/PaymentRequest/store/usePaymentRequestStore'
import { RateType } from '@/enums'
import BillCreateComplianceNotices from '@/components/compliances/hooks/BillCreateComplianceNotices.vue'
import { PaymentRequestTypeEnum, TimesheetTypeEnum } from '@/components/PaymentRequest/enums'
import Submit from '@/components/PaymentRequest/Submit.vue'
import { useTimesheetStore } from '@/components/PaymentRequest/store/useTimesheetStore'
import { useValidationStore } from '@/components/PaymentRequest/store/useValidationStore'
import { useCalculationsStore } from '@/components/PaymentRequest/store/useCalculationsStore'
import { useExpensesStore } from '@/components/PaymentRequest/store/useExpensesStore'
import { useTaxStore } from '@/components/PaymentRequest/store/useTaxStore'
import { useTranslation } from '@/composables/translation'

interface Props {
  isOpen: boolean
  hireId: number
  billId?: number
  enableExpenseReports?: boolean
  requireTimesheets?: boolean
  vatTerminology: string
  vatRate: number
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
  enableExpenseReports: false,
  requireTimesheets: false,
})

const { $t, $tc } = useTranslation()

const submitFormRef = ref<InstanceType<typeof Submit> | null>(null)
const saveAndGoToExpenses = () =>
  // @ts-ignore
  submitFormRef.value?.submitForm('/bills/draft?redirect_uri=/billing/expenses')

const emit = defineEmits(['cancel'])

const store = usePaymentRequestStore()
const timesheetsStore = useTimesheetStore()
const validationStore = useValidationStore()
const calculationsStore = useCalculationsStore()
const expensesStore = useExpensesStore()
const taxStore = useTaxStore()

// Update the stores using the prop values
/* eslint-disable vue/no-setup-props-destructure */
timesheetsStore.requireTimesheets = props.requireTimesheets
store.vatRate = props.vatRate
store.vatTerminology = props.vatTerminology
expensesStore.enableExpenseReports = props.enableExpenseReports
store.billId = props.billId || 0
/* eslint-enable vue/no-setup-props-destructure */

const rateUnitsTerminology = {
  [RateType.HOURLY]: $t('hours'),
  [RateType.DAILY]: $t('days'),
  [RateType.WEEKLY]: $t('weeks'),
  [RateType.MONTHLY]: $t('months'),
  [RateType.FIXED]: $t('fixed'),
}
const rateUnits = computed(() => rateUnitsTerminology[store.paymentRequestRate.rateType])

const inputCurrencyConfig = computed(() => ({
  currencySymbol: store.currency,
  thousandsSeparator: store.currencyConfig.separator,
  decimalSeparator: store.currencyConfig.decimal,
  showFractions: true,
}))

const dateRange = computed(() => {
  const range = useDateRange(
    moment(store.paymentRequestForm.dateStart, 'YYYY-MM-DD'),
    store.paymentRequestForm.dateEnd
      ? moment(store.paymentRequestForm.dateEnd, 'YYYY-MM-DD')
      : undefined,
  )
  return range.value
})

const timesheetErrors = computed(
  () =>
    validationStore.v$.timesheetType.$errors?.[0]?.$message ||
    validationStore.v$.timesheetSpreadsheetValue.$errors?.[0]?.$message ||
    validationStore.v$.timesheetFiles.$errors?.[0]?.$message,
)

const cancelPaymentRequest = () => {
  store.clearPaymentRequest()
  expensesStore.clearExpenses()
  timesheetsStore.clearTimesheets()
  validationStore.v$.$reset()
  emit('cancel')
}

const { result, onResult, error, loading, refetch } = useQuery(
  gql`
    query HireAndBill($hireLocalId: Int!, $billLocalId: Int!) {
      hire(localId: $hireLocalId) {
        localId
        threadId
        rate
        ratetype
        ratetypeName
        ratetypeNameSlash
        currency
        freelancerFee
        market
        hasFreelancerVat
        yjob {
          name
          localId
        }
        company {
          name
        }
      }
      me {
        globalId
        activeAccount {
          globalId
          ... on HasBills {
            bill(localId: $billLocalId) {
              globalId
              localId
              isDraft
              isIssued
              isApproved
              isRejected
              isCancelled
              updatedAt
              dateStart
              dateEnd
              message #comment
              rate # used in case of fixed rate
              rateType # pull from hire, not here
              rateTypeName
              ratetypeNameSlash
              hours
              days
              weeks
              timeSheet
              timeSheetFormat
              tax_lines {
                name
                type
                value
              }
              files(first: 10, page: 1) {
                data {
                  globalId
                  localId
                  name
                  storageName
                  icon {
                    icon
                    color
                    thumbnail
                  }
                }
              }
              expenseReports(first: 10, page: 1) {
                data {
                  globalId
                  localId
                  title
                  total
                }
              }
            }
          }
        }
      }
    }
  `,
  {
    billLocalId: store.billId,
    hireLocalId: props.hireId,
  },
  () => ({
    // clientId: 'publicApi',
    fetchPolicy: 'no-cache',
    enabled: props.isOpen,
  }),
)

// const apiErrors = computed(() => error.value ?? 'no errors')
const persistedBill = ref(null)
const isDraft = ref(false)
const isApproved = ref(false)
const isEditable = ref(true)
const updatedAt = ref(null)
const hireRateTypeIsFixed = ref(false)
const persistedBillRateTypeIsFixed = ref(false)

// eslint-disable-next-line @typescript-eslint/no-shadow -- Baseline eslint ignore please fix if working on this
onResult((result) => {
  const hire = result?.data?.hire ?? {}
  const bill = result?.data?.me?.activeAccount?.bill ?? {}
  if (!hire.localId) return
  store.hasFreelancerVat = hire.hasFreelancerVat
  store.market = hire.market
  store.currency = hire.currency
  store.worksomeFee = hire.freelancerFee
  store.threadId = hire.threadId
  store.jobId = hire.yjob.localId
  store.jobName = hire.yjob.name
  store.companyName = hire.company.name
  store.hireRate = {
    rate: hire.rate,
    rateType: hire.ratetype,
    rateTypeName: hire.ratetypeName,
    rateTypeNameSlash: hire.ratetypeNameSlash,
  }
  store.paymentRequestRate = {
    rate: hire.rate,
    rateType: hire.ratetype,
    rateTypeName: hire.ratetypeName,
    rateTypeNameSlash: hire.ratetypeNameSlash,
  }
  hireRateTypeIsFixed.value = hire.ratetype === RateType.FIXED
  store.updatePaymentRequestType(
    hire.ratetype === RateType.FIXED
      ? PaymentRequestTypeEnum.SPECIFY_AMOUNT
      : PaymentRequestTypeEnum.TIME_X_RATE,
  )
  if (!bill.localId) return
  persistedBill.value = bill
  updatedAt.value = bill.updatedAt
  if (bill.isDraft) {
    store.paymentRequestStatus = 'draft'
    isDraft.value = true
  }
  if (bill.isIssued) {
    store.paymentRequestStatus = 'issued'
  }
  if (bill.isApproved) {
    store.paymentRequestStatus = 'approved'
    isApproved.value = true
  }
  if (bill.isApproved || bill.isRejected || bill.isCancelled) {
    isEditable.value = false
  }
  store.paymentRequestForm.comments = bill.message
  switch (bill.rateType) {
    case RateType.FIXED:
    case RateType.MONTHLY:
      persistedBillRateTypeIsFixed.value =
        bill.rateType === RateType.FIXED || bill.rateType === RateType.MONTHLY
      store.updatePaymentRequestType(PaymentRequestTypeEnum.SPECIFY_AMOUNT)
      // since the fixed amount is stored in the rate field, we need to
      // set the billableTotal from that
      if (bill.rate > 0) {
        store.paymentRequestForm.billableTotal = bill.rate.toFixed(2)
      }
      break
    case RateType.HOURLY:
      store.updatePaymentRequestType(PaymentRequestTypeEnum.TIME_X_RATE)
      store.paymentRequestForm.billableTime = bill.hours
      break
    case RateType.DAILY:
      store.updatePaymentRequestType(PaymentRequestTypeEnum.TIME_X_RATE)
      store.paymentRequestForm.billableTime = bill.days
      break
    case RateType.WEEKLY:
      store.updatePaymentRequestType(PaymentRequestTypeEnum.TIME_X_RATE)
      store.paymentRequestForm.billableTime = bill.weeks
      break
  }
  // Support old bills which were allowed a different ratetype than the hire contract
  if (
    bill.rateType !== RateType.MONTHLY &&
    (bill.rate !== hire.rate || bill.rateType !== hire.ratetype)
  ) {
    store.paymentRequestRate = {
      rate: bill.rate,
      rateType: bill.rateType,
      rateTypeName: bill.rateTypeName,
      rateTypeNameSlash: bill.ratetypeNameSlash,
    }
  }
  if (bill.dateStart) {
    store.paymentRequestForm.dateStart = moment(bill.dateStart, 'YYYY-MM-DD').format('YYYY-MM-DD')
  }
  if (bill.dateEnd) {
    store.paymentRequestForm.dateEnd = moment(bill.dateEnd, 'YYYY-MM-DD').format('YYYY-MM-DD')
  }
  expensesStore.updateExpenseReports(
    bill.expenseReports?.data?.map((expenseReport) => ({
      globalId: expenseReport.globalId,
      localId: expenseReport.localId,
      title: expenseReport.title,
      total: expenseReport.total,
    })) || [],
  )
  timesheetsStore.timesheetType =
    timesheetsStore.timesheetTypeOptions.find(
      (timesheetTypeOption) => timesheetTypeOption.value === bill.timeSheetFormat,
    ) || timesheetsStore.timesheetTypeOptions[0]
  if (bill.timeSheetFormat === TimesheetTypeEnum.SHEET) {
    timesheetsStore.timesheetSpreadsheetValue = bill.timeSheet
  }
  if (bill.timeSheetFormat === TimesheetTypeEnum.TEXT) {
    timesheetsStore.timesheetComment = JSON.parse(bill.timeSheet)
  }
  if (bill.timeSheetFormat === TimesheetTypeEnum.FILE) {
    timesheetsStore.timesheetFiles = bill.files?.data
  }
  bill.tax_lines?.forEach((taxLine) =>
    taxStore.patchTaxLine({
      rate: taxLine.value / 100,
      terminology: taxLine.name,
    }),
  )
})

/**
 * We must support bills which were made in a more flexible system
 * which allowed for fixed rate hires to receive bills with 'Time X Rate'
 * and also allowed a rate which is different from the rate on the hire
 */
const allowTimeXRatePaymentRequests = computed(() => {
  // When hire is fixed, and either no bill, or bill is also fixed
  if (hireRateTypeIsFixed.value && (!persistedBill.value || persistedBillRateTypeIsFixed.value)) {
    return false
  }
  // If hire is TimeXRate, we can bill both types regardless of
  // If persisted bill doesn't exist, is fixed, or is TimeXRate
  return true
})

const updatePaymentRequestType = (newPaymentRequestType: PaymentRequestTypeEnum) => {
  if (newPaymentRequestType === PaymentRequestTypeEnum.SPECIFY_AMOUNT) {
    store.paymentRequestRate.rateType = RateType.FIXED
  } else {
    store.paymentRequestRate.rate = store.hireRate.rate
    store.paymentRequestRate.rateType = store.hireRate.rateType
  }
  store.updatePaymentRequestType(newPaymentRequestType)
}
watch(
  () => loading.value ?? false,
  (isFetching) => {
    store.isFetchingData = isFetching
  },
  { immediate: true },
)
watch(
  () => props.isOpen,
  (newValue, oldValue) => {
    if (!oldValue && newValue) {
      refetch()
    }
  },
  { immediate: true },
)
</script>

<style scoped lang="scss">
@tailwind base;

:deep(.wds-modal-body) {
  @apply tw-px-10;
}
:deep(.wds-text) {
  .wds-text-primary {
    @apply tw-text-gray-850;
  }
  .wds-text-secondary .tw-text-base {
    @apply tw-text-gray-600;
  }
}
:deep(.wds-collapse-body-inner) {
  padding: 1.5rem 1rem !important;
}

.payment-request-form-container {
  @apply tw-my-8.5;
  @apply tw-gap-8.5;
}
.payment-request-input-section {
  @apply tw-p-4;
  @apply tw-bg-true-gray-50;
}
.emphasised-amount-line {
  @apply tw-font-medium;
  @apply tw-text-gray-900;
}
</style>
